
import {
    Vue,
    Component,
    Prop,
    Emit,
    Watch,
    Ref,
} from 'vue-property-decorator';

type KeyboardType = 'province' | 'mixin' | 'number' | 'spacial';
const StorageHistoryPlateKey = '_rryb_webpay_history_plates_';
const StorageHistoryPlateMaxLen = 3;
const PlateMaxLen = 8;

@Component({
    name: 'PlateKeyboard',
    components: {},
})
export default class PlateKeyboard extends Vue {

    public $prompt!: any;

    @Ref('input')
    public readonly input!: HTMLDivElement;

    @Ref('history')
    public readonly history!: HTMLDivElement;

    @Ref('keyboard')
    public readonly keyboard!: HTMLDivElement;

    // 默认车牌
    @Prop({
        type: String,
        default: '',
    })
    private defaultPlate!: string;

    // 默认选中省份
    @Prop({
        type: String,
        default: '桂',
    })
    private defaultProvince!: string;

    // 显示历史车牌
    @Prop({
        type: Boolean,
        default: true,
    })
    private showHistoryPlate!: boolean;

    // 车牌占位符
    @Prop({
        type: String,
    })
    private placeholder?: string;

    // 车牌最大长度
    private plateLen: number = PlateMaxLen;
    // 车牌值数组
    private plateArr: string[] = [this.defaultProvince, ...new Array(PlateMaxLen - 1).fill('')];
    // 车位输入框占位符集
    private get currentPlaceholder(): string[] {
        const p = this.placeholder === void 0 ? '输入车牌缴费' : this.placeholder.slice(0, 6);
        const arr = p.split('');

        if (arr.length < 6) {
            arr.push(...new Array(6 - arr.length).fill(''));
        }

        return [this.defaultProvince, ...arr, '新能源'];
    }
    // 车牌输入框焦点序号
    private focusIndex: number | null = null;
    // 键盘类型
    private keyboardType: KeyboardType = 'province';
    // 省份键盘
    private province: string[] = [
        '京', '津', '沪', '渝', '蒙', '新', '藏',
        '黑', '吉', '辽', '晋', '冀', '青', '鲁',
        '豫', '苏', '皖', '浙', '闽', '赣', '湘',
        '鄂', '桂', '粤', '琼', '宁', '甘', '陕',
        '贵', '云', '川', '临'];
    // 特殊键盘
    private special: string[] = [
        '港', '澳', '学',
        '领', '警', '军',
        '使', 'WJ', '应急'];
    // 混合键盘
    private mixin: string[] = [
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
        'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K',
        'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V',
        'W', 'X', 'Y', 'Z'];
    // 纯数字键盘
    private num: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    // 历史车牌集
    private historyPlates: string[] = [];

    get currentPlate(): string {
        return this.plateArr.join('');
    }

    @Watch('focusIndex')
    public focusIndexChange(n: any, o: any) {
        if (n === null) {
            this.close();
        } else if (o === null) {
            this.open();
        } else {
            this.dispatchKeyboardType();
        }
    }

    @Watch('defaultPlate')
    public defaultPlateChange(n: any, o: any) {
        if (typeof n === 'string' && n) {
            this.transform(n);
        }
    }

    // 车牌输入框点击 打开相应键盘
    public handleInputItemClick(index: number): void {
        this.focusIndex = index;
        this.dispatchKeyboardType();
    }

    // 关闭键盘
    public closeKeyboard(e: Event): void {
        this.focusIndex = null;
        e.stopPropagation();
    }

    // 点击键盘输入车牌
    public handleKeyboardClick(key: string): void {
        const focusIndex = this.focusIndex || 0;
        this.$set(this.plateArr, focusIndex, key);
        if (focusIndex < this.plateLen) {
            this.focusIndex = focusIndex + 1;
        }
        this.dispatchKeyboardType();
    }

    /**
     * 键盘类型调度
     * @param {KeyboardType} type 按type调起相应键盘，不传则按规则调起
     */
    public dispatchKeyboardType(type?: KeyboardType): void {
        if (type) {
            this.keyboardType = type;
            return;
        }

        const focusIndex = this.focusIndex || 0;
        const plates = this.plateArr;

        switch (focusIndex) {
            case 0:
                this.keyboardType = 'province';
                break;
            case 1:
                if (this.special.includes(plates[0])) {
                    this.keyboardType = 'province';
                } else {
                    this.keyboardType = 'mixin';
                }
                break;
            case 2:
                // no break;
            case 3:
                // no break;
            case 4:
                // no break;
            case 5:
                // no break;
            case 6:
                // no break;
            case 7:
                // no break;
            default:
                if (this.keyboardType !== 'number') {
                    this.keyboardType = 'mixin';
                }
                break;
        }
    }

    // 移除
    public remove(): void {
        const focusIndex = this.focusIndex;
        if (focusIndex !== null) {
            if (this.plateArr[focusIndex]) {
                this.$set(this.plateArr, focusIndex, '');
            } else if (focusIndex) {
                this.focusIndex = focusIndex as number - 1;
                this.$set(this.plateArr, this.focusIndex, '');
            }
        }
    }

    // 车牌转换格式
    public transform(plate: string): string[] {
        if (!plate) {
            return [];
        }
        let splitStr: string[];
        // 解析特殊车牌，如： WJ浙0002J, 桂A0105应急，普通车牌正常拆分
        if (plate.match(/^WJ/)) {
            plate = plate.replace(/^WJ/, (val: string) => `${val},`);
            splitStr = [plate.split(',')[0], ...plate.split(',')[1].split('')];
        } else if (plate.match(/应急$/)) {
            plate = plate.replace(/应急$/, (val: string) => `,${val}`);
            splitStr = [...plate.split(',')[0].split(''), plate.split(',')[1]];
        } else {
            splitStr = plate.split('');
        }
        // 只返回前8位
        return splitStr.slice(0, 8).map((str: string, index: number) => {
            this.$set(this.plateArr, index, str);
            return str;
        });
    }

    // 添加历史车牌
    public insertHistoryPlate(plate: string): void {
        const plates = this.historyPlates;
        const index = plates.indexOf(plate);
        if (index !== -1) {
            plates.splice(index, 1);
        }

        plates.unshift(plate);
        this.historyPlates = plates.slice(0, StorageHistoryPlateMaxLen);
        localStorage.setItem(
            StorageHistoryPlateKey,
            this.historyPlates.join(','),
        );
    }

    // 清除历史车牌
    public clearHistoryPlate(): void {
        localStorage.removeItem(StorageHistoryPlateKey);
        while (this.historyPlates.length) {
            this.historyPlates.shift();
        }
    }

    // 手动输入完整车牌
    public manualInput(): void {
        this.$prompt({
            title: '请输入完整车牌',
        }).then(({ value: input }: any) => {
            if (input != null) {
            input.toUpperCase().split('').forEach((item: string, index: number) => {
                this.$set(this.plateArr, index, item ?? '');
            });
        }
        }).catch(() => { /** do nothing */ });

        /** @important 微信浏览器X5内核BUG: prompt返回值恒为null */
        // const input = prompt('请输入完整车牌');
        // if (input != null) {
        //     input.split('').forEach((item: string, index: number) => {
        //         this.$set(this.plateArr, index, item ?? '');
        //     });
        // }
    }

    @Emit('confirm')
    public search(plate?: string): string {
        return plate || this.plateArr.join('');
    }

    @Watch('plateArr', { deep: true })
    @Emit('change')
    public onChange() {
        return this.plateArr.join('');
    }

    // 调起键盘
    @Emit('open')
    public open() {
        /** empty block */
    }

    // 调起键盘动画结束
    @Emit('opened')
    public opened() {
        /** empty block */
    }

    // 收起键盘
    @Emit('close')
    public close() {
        /** empty block */
    }

    // 收起键盘动画结束
    @Emit('closed')
    public closed() {
        /** empty block */
    }

    private mounted() {
        // 转换默认车牌
        if (this.defaultPlate) {
            this.transform(this.defaultPlate);
        }
        // 写入缓存历史车牌
        const history = localStorage.getItem(StorageHistoryPlateKey) || '';
        if (history) {
            history.split(',')
                .slice(0, StorageHistoryPlateMaxLen)
                .forEach((item: string, index: number) => {
                    this.$set(this.historyPlates, index, item);
                });
        }
        // 添加全局关闭键盘事件
        document.addEventListener('click', this.closeKeyboard);
        document.addEventListener('touchstart', this.closeKeyboard);
    }

    private beforeDestroy() {
        document.removeEventListener('click', this.closeKeyboard);
        document.removeEventListener('touchstart', this.closeKeyboard);
    }

}
